import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { saveAs } from "file-saver";
import ACTIONS from "../store/action";
import { Upload, Table, Button, Space, Modal, Form, Row, Col, Radio, Typography } from "antd";
import {
  DownloadOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  CloseOutlined
} from "@ant-design/icons";
import moment from "moment";
import {
  selectCanViewOrDownloadPatientAttachments,
  selectIsFetching,
  selectPatientAttachments,
  selectUsers,
  selectSelectedOrderId,
  selectPatientCycles
} from "../store/selector";
import { ApiNames, viewPatientAttachmentURL } from "../api/api";
import { dateSort, nullSort, stringSort } from "../utils/sort";
import { SORT_DIRECTIONS } from "../constant/sort";
import { downloadPatientAttachmentsAPI } from "../api/api";
import "./PatientAttachmentTab.css";

import { CycleFormItem } from "./form/CycleFormItem";
import { DATE_FORMAT_2 } from "../utils/date";
import { cleanupAndFormatStrings } from "../utils/formaters";
import { getDefaultCycleNumber } from "../utils/cycles";
import styled from "styled-components";
import { TableStyle } from "./SavedList/TableStyle";
import { PatientViewTableStyle } from "./SavedList/PatientViewTableStyle";
import ErrorMessage from "./customComponent/customMessages/ErrorMessage";
import SuccessMessage from "./customComponent/customMessages/SuccessMessage";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { ModalComponent } from "./customComponent/ModalComponent";
import { ReactComponent as DeleteIcon } from "../assets/svg/delete-new-icon.svg";
import { scrollTableToTop } from "../utils/misc";
import { BLACK } from "../constant/colors";
import AttachmentPermissionsPopover from "./AttachmentPermissionsPopover";
import { allSupportedFileTypes, MAX_FILE_SIZE, MB } from "../constant/attachmentFile";

const StyledTable = styled(Table)`
  ${TableStyle}
  ${PatientViewTableStyle}

  .ant-table {
    margin: 0;
    margin-top: 10px !important;
    margin-bottom: 30px !important;
    height: ${(props) => (props.isFormOpen ? `calc(100vh - 420px)` : `calc(100vh - 330px)`)};
  }

  .ant-table-empty .ant-table-tbody {
    height: ${(props) => (props.isFormOpen ? `calc(100vh - 460px)` : `calc(100vh - 375px)`)};
  }

  .ant-table-tbody > tr > td:first-child {
    padding: 8px 0px 8px 20px !important;
  }

  .ant-table-tbody > tr > td:nth-child(2) {
    padding: 8px 0px 8px 8px !important;
  }
`;

const ReachableContext = React.createContext();
const { Text } = Typography;

const AttachmentType = Object.freeze({
  CYCLE: "CYCLE",
  PATIENT: "PATIENT"
});

const PatientAttachmentTabComponent = ({
  t,
  patient,
  users,
  PatientAttachments,
  isFetching,
  addPatientAttachmentsAct,
  deletePatientAttachmentsAct
}) => {
  const [attachmentType, setAttachmentType] = useState(AttachmentType.CYCLE);
  const [fileList, setFileList] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [modal, contextHolder] = Modal.useModal();
  const [form] = Form.useForm();

  const tableId = "attachments-table";

  const canViewOrDownloadPatientAttachments = useSelector(selectCanViewOrDownloadPatientAttachments);
  const selectedOrderId = useSelector(selectSelectedOrderId);
  const cycles = useSelector(selectPatientCycles);

  const onDelete = (record) => {
    // Use patient.id as fallback when record.pharmacyPatient is missing (for attachments added from FNC)
    const pharmacyPatientId = record.pharmacyPatient || patient.id;
    deletePatientAttachmentsAct(record.id, record.attachmentUniqueId, pharmacyPatientId);
  };

  const onDownload = async (record) => {
    const attachmentsDownloadRes = await downloadPatientAttachmentsAPI(record.id, record.attachmentUniqueId);
    const fileNameHeader = attachmentsDownloadRes.headers["Content-Disposition"];
    const fileName = fileNameHeader ? fileNameHeader.match(/\sfilename=(.+)(\s|$)/)[1] : record.filename;

    saveAs(attachmentsDownloadRes.data, fileName);
    if (attachmentsDownloadRes.data) {
      SuccessMessage("File dowloaded successfully");
    } else {
      ErrorMessage(`File dowloaded failed: ${attachmentsDownloadRes && attachmentsDownloadRes.errors[0].message}`);
    }
  };

  const columns = [
    {
      title: t("patient.attachments.col_title_date"),
      dataIndex: "createdAt",
      width: 150,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => dateSort(a.createdAt, b.createdAt, sortDirection),
      render: (text, record) => moment(record.createdAt).format("MMM D, YYYY")
    },
    {
      title: t("patient.attachments.col_title_user"),
      dataIndex: "user",
      width: 150,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => {
        const aUser = users[a.user];
        const bUser = users[b.user];
        return stringSort(
          `${aUser.firstName} ${aUser.lastName}`,
          `${bUser.firstName} ${bUser.lastName}`,
          sortDirection
        );
      },
      render: (text, record) => {
        const user = users[record.user];
        return cleanupAndFormatStrings(user?.firstName, user?.lastName);
      },
      onCell: () => ({
        id: "patient_attachment_added_by"
      })
    },
    {
      title: t("patient.attachments.col_title_cycle"),
      dataIndex: "cycle",
      width: 80,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) =>
        nullSort(
          a.pharmacyPatientOrderIndex,
          b.pharmacyPatientOrderIndex,
          sortDirection,
          () => a.pharmacyPatientOrderIndex - b.pharmacyPatientOrderIndex
        ),
      render: (text, record) => (record.pharmacyPatientOrderIndex ? record.pharmacyPatientOrderIndex : "")
    },
    {
      title: t("patient.attachments.col_title_admin_date"),
      dataIndex: "administrationDate",
      width: 150,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) =>
        dateSort(a?.cycle?.administrationDate, b?.cycle?.administrationDate, sortDirection),
      render: (text, record) =>
        record?.cycle?.administrationDate && moment(record?.cycle?.administrationDate).format(DATE_FORMAT_2)
    },
    {
      title: t("patient.attachments.col_title_attachment"),
      dataIndex: "filename",
      sortDirections: SORT_DIRECTIONS,
      render: (text, record) => {
        const href = viewPatientAttachmentURL(record.id, record.attachmentUniqueId);

        return (
          <AttachmentPermissionsPopover canViewOrDownload={canViewOrDownloadPatientAttachments}>
            <a
              href={canViewOrDownloadPatientAttachments ? href : "#"}
              onClick={(e) => !canViewOrDownloadPatientAttachments && e.preventDefault()}
              target="_blank"
              rel="noreferrer"
              style={{
                color: BLACK,
                opacity: 0.85,
                borderBottom: "1px solid",
                cursor: canViewOrDownloadPatientAttachments ? "pointer" : "not-allowed"
              }}
              id="patient_attachment_name_list"
            >
              {record.filename}
            </a>
          </AttachmentPermissionsPopover>
        );
      },
      sorter: (a, b, sortDirection) => stringSort(a.filename, b.filename, sortDirection)
    },
    {
      dataIndex: "download",
      width: 40,
      align: "center",
      render: (text, record) =>
        hoveredRow === record.key ? (
          <AttachmentPermissionsPopover canViewOrDownload={canViewOrDownloadPatientAttachments}>
            <DownloadOutlined
              style={{
                color: "#2B56A3",
                fontSize: 16,
                cursor: canViewOrDownloadPatientAttachments ? "pointer" : "not-allowed"
              }}
            />
          </AttachmentPermissionsPopover>
        ) : null,
      onCell: (record, idx) => ({
        id: `pt-attchmnt-download-${idx}`,
        onClick: () => {
          if (canViewOrDownloadPatientAttachments)
            modal.confirm({
              centered: true,
              className: "formPopupsModal",
              icon: <WarningIcon />,
              okText: t("yes_sure"),
              cancelText: t("back"),
              title: t("patient.attachments.confirm_download_title"),
              content: t("patient.attachments.confirm_download_content"),
              onOk: () => onDownload(record)
            });
        }
      })
    },
    {
      dataIndex: "delete",
      width: 60,
      align: "center",
      render: (text, record) =>
        hoveredRow === record.key ? (
          <AttachmentPermissionsPopover canViewOrDownload={canViewOrDownloadPatientAttachments}>
            <DeleteOutlined
              style={{
                color: "#2B56A3",
                fontSize: 16,
                cursor: canViewOrDownloadPatientAttachments ? "pointer" : "not-allowed"
              }}
            />
          </AttachmentPermissionsPopover>
        ) : (
          ""
        ),
      onCell: (record, idx) => ({
        id: `pt-attchmnt-delete-${idx}`,
        onClick: () => {
          if (canViewOrDownloadPatientAttachments) {
            setSelectedRow(record);
            setShowDeleteModal(true);
          }
        }
      })
    }
  ];

  const handleUpload = () => {
    fileList.forEach((file) => {
      addPatientAttachmentsAct(
        patient.patient.id,
        patient.id,
        file,
        attachmentType === AttachmentType.CYCLE ? form.getFieldValue("cycle") : null
      );
    });
    setFileList([]);
  };

  const uploadProps = {
    name: "file",
    multiple: true,
    accept: allSupportedFileTypes,
    itemRender: (originNode, file) => (
      <>
        <Text style={{ maxWidth: "85%", padding: 2, color: "#9B9B9B" }} id="patient_attachment_name">
          {file && file.name}
        </Text>
        <CloseOutlined onClick={() => onRemove(file)} />
      </>
    ),
    beforeUpload: (_, fileList) => handleFileUpload(fileList, MAX_FILE_SIZE, ErrorMessage, setFileList),
    fileList
  };

  const isFileSizeValid = (file, maxSize) => file.size < maxSize;

  const separateFiles = (fileList, maxSize) => {
    return fileList.reduce(
      (acc, file) => {
        if (isFileSizeValid(file, maxSize)) {
          acc.validFiles.push(file);
        } else {
          acc.invalidFiles.push(file);
        }
        return acc;
      },
      { validFiles: [], invalidFiles: [] }
    );
  };

  const handleFileUpload = (fileList, maxSize, errorMessage, setFileListFunc) => {
    const { validFiles, invalidFiles } = separateFiles(fileList, maxSize);
    invalidFiles.map((file) =>
      errorMessage(
        t("patient.attachments.file_size_exceeds", {
          maxSize: MAX_FILE_SIZE / MB,
          fileSize: (file.size / MB).toFixed(2)
        })
      )
    );
    setFileListFunc(validFiles);
  };

  const onRemove = (file) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const onTableChange = () => {
    scrollTableToTop(tableId);
  };

  return (
    <ReachableContext.Provider value={null}>
      <Space direction={"vertical"} size={"large"} style={{ width: "87vw", marginBottom: "20" }}>
        {isFormOpen ? (
          <Form
            name="newAttachment"
            form={form}
            initialValues={{
              cycle: getDefaultCycleNumber(selectedOrderId, cycles, patient.currentOrder)
            }}
            style={{ width: "100%" }}
          >
            <Row align="middle" style={{ width: "100%", backgroundColor: "#F9FBFF", padding: 16 }}>
              <Col
                span={12}
                style={{
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  padding: 10,
                  marginRight: 5,
                  height: "8vh",
                  overflow: "auto"
                }}
              >
                <Upload {...uploadProps}>
                  <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                    <Button type="primary" icon={<FolderOpenOutlined />} disabled={isFetching} loading={isFetching} />
                  </div>
                </Upload>
              </Col>
              <Col span={11}>
                <Space>
                  <Radio.Group
                    buttonStyle="solid"
                    size="large"
                    name="attachmentType"
                    defaultValue={attachmentType}
                    onChange={(e) => setAttachmentType(e.target.value)}
                  >
                    <Radio data-tm-id="at-per-patient-btn" value={AttachmentType.PATIENT}>
                      {t("form.cycle.per_patient")}
                    </Radio>

                    <Radio data-tm-id="at-per-cycle-btn" value={AttachmentType.CYCLE}>
                      {t("form.cycle.per_cycle")}
                    </Radio>
                  </Radio.Group>
                  {attachmentType === AttachmentType.CYCLE && (
                    <CycleFormItem
                      required={true}
                      style={{ marginBottom: 0 }}
                      patient={patient}
                      dataTMId="at-current-drop-down"
                    />
                  )}
                </Space>
              </Col>
            </Row>
            <Row
              justify="end"
              style={{
                width: "100%",
                backgroundColor: "#F9FBFF",
                padding: "0px 16px 0px 16px"
              }}
            >
              <Form.Item>
                <Button
                  type="text"
                  id="pt-attchmnt-reset-btn"
                  onClick={() => {
                    form.resetFields();
                    setFileList([]);
                    setIsFormOpen(false);
                  }}
                >
                  <Text style={{ color: "#2B56A3" }}>{t("cancel")}</Text>
                </Button>
                <Button
                  onClick={handleUpload}
                  id="pt-attchmnt-submit-btn"
                  icon={<PaperClipOutlined />}
                  disabled={fileList.length <= 0 || isFetching}
                  loading={isFetching}
                  type="primary"
                >
                  {t("upload")}
                </Button>
              </Form.Item>
            </Row>
          </Form>
        ) : (
          <Row>
            <Button
              icon={<FolderOpenOutlined style={{ fontSize: 12, marginRight: "" }} />}
              onClick={() => setIsFormOpen(true)}
              type="primary"
              data-tm-id="at-browse-btn"
            >
              {t("patient.attachments.browse")}
            </Button>
            <Text
              style={{
                marginLeft: "1em",
                marginTop: "0.5em",
                color: "#9B9B9B"
              }}
            >
              {t("patient.attachments.max_file_size")}
            </Text>
          </Row>
        )}
      </Space>
      <StyledTable
        id={tableId}
        bordered={false}
        columns={columns}
        isFormOpen={isFormOpen}
        dataSource={PatientAttachments.map((attachment, index) => ({
          ...attachment,
          key: index
        }))}
        pagination={false}
        onChange={onTableChange}
        onRow={(record) => {
          return {
            onMouseEnter: () => {
              setHoveredRow(record.key);
            },
            onMouseLeave: () => {
              setHoveredRow(null);
            }
          };
        }}
      />
      <ModalComponent
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText={t("ok_text")}
        onOk={() => {
          onDelete(selectedRow);
          setShowDeleteModal(false);
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("patient.attachments.confirm_delete_title")}</Text>
          <Text className="content">{t("patient.attachments.confirm_delete_content")}</Text>
        </Space>
      </ModalComponent>
      {contextHolder}
    </ReachableContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({
  PatientAttachments: selectPatientAttachments,
  users: selectUsers,
  isFetching: selectIsFetching([ApiNames.AttachmentsUpload, ApiNames.Attachment])
});

const mapDispatchToProps = (dispatch) => ({
  addPatientAttachmentsAct: (patientId, pharmacyPatient, file, orederIndex) =>
    dispatch(ACTIONS.addPatientAttachmentsAction(patientId, pharmacyPatient, file, orederIndex)),
  deletePatientAttachmentsAct: (id, attachmentUniqueId, pharmacyPatient) =>
    dispatch(ACTIONS.deletePatientAttachmentsAction(id, attachmentUniqueId, pharmacyPatient))
});

const PatientAttachmentTab = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(PatientAttachmentTabComponent));

export { PatientAttachmentTab };
